const USER_API_ROOT = window.location.host.startsWith('localhost')
    ? `http://127.0.0.1:8000`
    : `http://157.249.76.125:9300`;


export function getPyaeroptions() {
    const url = `${USER_API_ROOT}/pyaeroptions`;
    return fetchJSON<Pyaeroptions>(url);
}

export function getUploadedFiles(accessToken: string) {
    const url = `${USER_API_ROOT}/get_uploaded_files`;
    return fetchJSON<UploadedFileData[]>(url, { accessToken },);
}

export function getUploadedFile(accessToken: string, fileId: number) {
    const url = `${USER_API_ROOT}/get_model_file_valid`;
    return fetchJSON<UploadedFileData>(url, {
        method: 'POST',
        accessToken,
        body: {
            file_id: fileId
        }
    });
}

export function getUserStatsData(accessToken: string) {
    const url = `${USER_API_ROOT}/get_user_stats`;
    return fetchJSON<UserStatsData>(url, { accessToken },);
}

export function getUserExperimentsData(accessToken: string) {
    const url = `${USER_API_ROOT}/get_user_experiments`;
    return fetchJSON<UserExperimentData[]>(url, { accessToken, },);
}

export function submitExperiment(accessToken: string, id: string) {
    const url = `${USER_API_ROOT}/add_user_experiment`;
    return fetchJSON<FileMetaData[]>(url, {
        method: 'POST',
        accessToken,
        body: {
            cfg_id: id
        }
    });
}

export function deleteExperiment(accessToken: string, experimentId: number) {
    const url = `${USER_API_ROOT}/delete_experiment`;
    return fetchJSON(url, {
        method: 'DELETE',
        accessToken,
        body: { experiment_id: experimentId },
    });
}

export async function getCfgFile(accessToken: string, experimentId: number) {
    const url = `${USER_API_ROOT}/get_cfg_file`;
    return fetchFile<FileMetaData[]>(url, {
        method: 'POST',
        accessToken,
        body: { experiment_id: experimentId }
    });
}

export function getFilesMetaData(accessToken: string, modelFileIds: number[]) {
    const url = `${USER_API_ROOT}/get_files_metadata`;
    return fetchJSON<FileMetaData[]>(url, {
        method: 'POST',
        accessToken,
        body: { model_files_id: modelFileIds }
    });
}

export function addCfgFile(accessToken: string, cfgForms: any) {
    const url = `${USER_API_ROOT}/add_cfg_file`;
    return fetchJSON<string>(url, {
        method: 'POST',
        accessToken,
        body: cfgForms
    });
}

export function getCfgFileMetaData(accessToken: string, id: string) {
    const url = `${USER_API_ROOT}/get_cfg_valid`;
    return fetchJSON<UploadedFileData>(url, {
        method: 'POST',
        accessToken,
        body: {
            cfg_id: id
        }
    });
}

export function deleteFile(accessToken: string, modelFileId: number) {
    const url = `${USER_API_ROOT}/remove_uploaded_file`;
    return fetchJSON(url, {
        method: 'DELETE',
        accessToken,
        body: { model_file_id: modelFileId },
    });
}

export function runEvaluationJob(accessToken: string, cfgForms: any) {
    const url = `${USER_API_ROOT}/runEvaluationJob`;
    return fetchJSON<EvaluationJob>(url, {
        method: 'POST',
        accessToken,
        body: cfgForms,
    });
}

export function getUser(accessToken: string) {
    const url = `${USER_API_ROOT}/get_user`;
    return fetchJSON<UserData>(url, { accessToken });
}

export function loginUser(mail: string, password: string) {
    const url = `${USER_API_ROOT}/token`;
    return fetchJSON<{ access_token: string }>(url, {
        method: 'POST',
        body: { username: mail, password }
    });
}

export function emailPasswordChangeRequest(mail: string, recaptcha: string) {
    const url = `${USER_API_ROOT}/email_password_change_request`;
    return fetchJSON<{ access_token: string }>(url, {
        method: 'POST',
        body: { mail, recaptcha }
    });
}

export function changePasswordUsingEmail(mail: string, newPassword: string, guid: string) {
    const url = `${USER_API_ROOT}/change_password_using_email`;
    return fetchJSON<{ access_token: string }>(url, {
        method: 'POST',
        body: { mail, new_password: newPassword, guid }
    });
}

export function addUser(
    firstName: string,
    lastName: string,
    mail: string,
    password: string,
    recaptcha: string
) {
    const url = `${USER_API_ROOT}/add_user`;
    return fetchJSON<{ access_token: string }>(url, {
        method: 'POST',
        body: { firstname: firstName, lastname: lastName, mail, password, recaptcha },
    });
}

export async function fetchJSON<T = unknown>(
    url: string,
    options: {
        method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
        accessToken?: string;
        body?: Record<string, any>;
        headers?: Record<string, string>;
    } = {}
): Promise<T> {
    const { method = 'GET', accessToken, body, headers = {} } = options;

    const fetchHeaders: Record<string, string> = {
        Accept: 'application/json',
        ...headers,
    };

    let requestBody: BodyInit | undefined;

    if (accessToken) {
        fetchHeaders['Authorization'] = `Bearer ${accessToken}`;
    }

    if (body && method !== 'GET') {
        fetchHeaders['Content-Type'] = 'application/json';
        requestBody = JSON.stringify(body);
    }

    const response = await fetch(url, {
        method,
        headers: fetchHeaders,
        body: requestBody,
        cache: 'no-cache',
    });

    if (!response.ok) {
        throw new Error(`${method} ${url} failed: ${response.status} ${response.statusText}`);
    }

    return (await response.json()) as T;
}


export interface DownloadFileMetaData {
    filename: string;
    size: number;
    type: string; // MIME type
}

export async function fetchFile<T = DownloadFileMetaData>(
    url: string,
    options: {
        method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
        accessToken?: string;
        body?: Record<string, any>;
        headers?: Record<string, string>;
        /** Optional: override the downloaded filename */
        downloadFilename?: string;
    } = {}
): Promise<T> {
    const { method = 'GET', accessToken, body, headers = {}, downloadFilename } = options;

    const fetchHeaders = new Headers({
        Accept: '*/*', // Important: don't assume JSON for file downloads
        ...headers,
    });

    let requestBody: BodyInit | undefined;

    if (accessToken) {
        fetchHeaders.append('Authorization', `Bearer ${accessToken}`);
    }

    if (body && method !== 'GET') {
        fetchHeaders.append('Content-Type', 'application/json');
        requestBody = JSON.stringify(body);
    }

    const response = await fetch(url, {
        method,
        headers: fetchHeaders,
        body: requestBody,
        cache: 'no-cache',
    });

    if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    // Extract filename from Content-Disposition header if available
    let filename = downloadFilename || 'download.json';
    const disposition = response.headers.get('Content-Disposition');
    if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/i);
        if (match?.[1]) {
            filename = match[1];
        }
    }

    // Fallback: use content-type to guess extension if needed
    const contentType = response.headers.get('Content-Type') || 'application/octet-stream';

    const blob = await response.blob();

    // Trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(downloadUrl);

    // Return metadata about the downloaded file
    const meta: DownloadFileMetaData = {
        filename,
        size: blob.size,
        type: blob.type || contentType,
    };

    return meta as unknown as T;
}